import axios from "axios";
import Vue from "vue";

const state = () => ({
  journeys: [],
  currentStep: null,
  steps: [],
  currentJourney: null,
  currentJourneySteps: [],
  isCreateFromDefault: false,
  needToRefreshCanvas: false,
  parentStepOptions: {},
});

const getters = {
    parentStepOptions: state => {
      return state.steps.reduce((steps, step) => {
        steps[step.id] = step.name;
        return steps;
      }, {});
    },
    journeys: state => state.journeys,
    steps: state => state.steps,
    currentJourney: state => state.currentJourney,
    currentJourneySteps: state => state.currentJourneySteps,
    currentJourneyStep: state => key => state.currentJourneySteps.find(step => step.key === key),
    currentStep: state => state.currentStep,
    needToRefreshCanvas: state => state.needToRefreshCanvas,
}

const actions = {
  async fetchCurrentStep({ commit }, stepId) {
    if (stepId) {
      try {
        const {data: step} = await axios.get(`/api/v1/steps/${stepId}`);
        commit('setCurrentStep', step);
      } catch (error) {
        console.error("Error fetching step data:", error);
      }
    } else {
      commit('setCurrentStep', newStep());
    }
  },
  async deleteStep({ commit }, stepId) {
    try {
      await axios.delete(`/api/v1/steps/${stepId}`);
      commit('removeStep', stepId);
    } catch (error) {
      console.error("Failed to delete step:", error);
      throw helper.formatError(error, "deleteStep");
    }
  },
  async saveStep({ commit }, formData) {
    try {
      let response;
      if (formData.id) {
        response = await axios.put(`/api/v1/steps/${formData.id}`, formData);
      } else {
        response = await axios.post("/api/v1/steps", formData);
      }
    } catch (error) {
      console.error("Error saving step:", error.response ? error.response.data : error.message);
      throw error;
    }
  },
  fetchJourneys: async ({ commit }) => {
    try {
      const { data: journeys } = await axios.get('/api/v1/journeys');
      commit('setJourneys', journeys);
      return true;
    } catch (error) {
      console.error("Failed to fetch journey list.");
      throw helper.formatError(error, "fetchJourneys");
    }
  },
  fetchJourney: async ({ commit, state }, journeyId) => {
    if (journeyId && journeyId !== 'new') {
      try {
        const { data: journey } = await axios.get(`/api/v1/journeys/${journeyId}/full`);
        commit('setCurrentJourney', journey);
        commit('setCurrentJourneySteps', journey.steps);
        return true;
      } catch (error) {
        console.error("Failed to fetch Journey.");
        throw helper.formatError(error, "fetchJourney");
      }
    } else {
      // commit empty journey
      if (state.isCreateFromDefault) {
        commit('removeIdAndKeyFromJourney');
      } else {
        commit('setCurrentJourney', newJourney());
        commit('setCurrentJourneySteps', []);
      }
      commit('setIsCreateFromDefault', false);
      return true;
    }
  },
  saveJourney: async({ commit, state }, journey) => {
    try {
      const journeyId = journey.id;
      journey["steps"] = state.currentJourneySteps;
      let result = null;
      if (!journeyId) {
        // create new journey
        result = await axios.post(`/api/v1/journeys`, journey);
      } else {
        // update journey
        result = await axios.put(`/api/v1/journeys/${journeyId}`, journey);
      }
      commit('setCurrentJourney', result.data);
      return result.data;
    } catch (error) {
      console.error("Failed to save journey.")
      throw helper.formatError(error, "saveJourney");
    }
  },
  deleteJourney: async({}, journeyId) => {
    try {
      const result = await axios.delete(`/api/v1/journeys/${journeyId}`);
      return result.data;
    } catch (error) {
      console.error("Failed to deleted journey.");
      throw helper.formatError(error, "deleteJourney");
    }
  },
  duplicateJourney: async({ commit, state }, journeyId) => {
    try {
      const { data: journey } = await axios.post(`/api/v1/journeys/${journeyId}/duplicate`);
      return journey;
    } catch (error) {
      console.error("Failed to duplicate journey.")
      throw helper.formatError(error, "duplicateJourney");
    }
  },
  fetchSteps: async({ commit }) => {
    try {
      const { data: steps } = await axios.get('/api/v1/steps');
      commit('setSteps', steps);
      return true;
    } catch (error) {
      console.error("Failed to fetch stpes.");
      throw helper.formatError(error, "fetchSteps");
    }
  },
  saveJourneySteps: async({ commit, state }) => {
    try {
      // TODO save whole journey steps
      const journeyId = state.currentJourney.id;
      if (!journeyId || journeyId === 'new') {
        return false
      }
      const response = await axios.put(`/api/v1/journeys/${journeyId}/steps`, state.currentJourneySteps);
      commit('setCurrentJourneySteps', response.data);
      return true;
    } catch (error) {
      console.error('Error Saving Steps: ', error);
      throw helper.formatError(error, "saveJourneySteps");
    }
  },
  saveJourneyStep: async({ dispatch, commit, state }, step) => {
    const journeyId = state.currentJourney.id;
    const index = state.currentJourneySteps.findIndex(currentJourneyStep => currentJourneyStep.key === step.key);
    const isNewStep = index === -1;
    try {
      const response = await dispatch('saveJourneyStepService', { journeyId, step, isNewStep });

      const updatedStep = response.data;

      if (isNewStep) {
        commit('addCurrentJourneySteps', { key: step.key, step: updatedStep });
      } else {
        commit('updateCurrentJourneyStep', updatedStep);
      }
      return true;
    } catch (error) {
      console.error('Error saving step: ', error);
      throw helper.formatError(error, "saveJourneyStep");
    }
  },
  saveJourneyStepService: ({}, { journeyId, step, isNewStep }) => {
    if (!journeyId || journeyId === 'new') {
      return { data: step };
    }

    if (isNewStep) {
      return axios.post(`/api/v1/journeys/${journeyId}/steps`, step);
    } else {
      return axios.put(`/api/v1/journeys/${journeyId}/steps/${step.id}`, step);
    }
  },
  deleteJourneyStep: async({ commit, state }, step) => {
    const journeyId = state.currentJourney.id;
    try {
      await axios.delete(`/api/v1/journeys/${journeyId}/steps/${step.id}`);
      commit('removeCurrentJourneyStep', step.key);
      return true;
    } catch (error) {
      console.error('Error saving step: ', error);
      throw helper.formatError(error, "deleteJourneyStep");
    }
  },
  saveJourneyAsDefault: async({}, journeyId) => {
    try {
      const result = await axios.post(`/api/v1/journeys/${journeyId}/default`);
      return true
    } catch (error) {
      console.error('Error save journey as default: ', error);
      throw helper.formatError(error, "saveJourneyAsDefault");
    }
  },
}

const mutations = {
  removeStep: (state, stepId) => {
    state.steps = state.steps.filter(step => step.id !== stepId);
  },
  setJourneys: (state, journeys) => {
    state.journeys = journeys;
  },
  setCurrentStep: (state, step) => {
    state.currentStep = { ...step };
  },
  setCurrentJourney: (state, journey) => {
    state.currentJourney = { ...journey };
  },
  removeIdAndKeyFromJourney: (state) => {
    state.currentJourney.id = null;
    state.currentJourney.key = null;
  },
  setCurrentJourneySteps: (state, steps) => {
    state.currentJourneySteps = steps;
  },
  addCurrentJourneySteps: (state, { key, step }) => {
    const index = state.currentJourneySteps.findIndex(step => step.key === key);
    if (index !== -1) {
      Vue.set(state.currentJourneySteps, index, step);
    } else {
      state.currentJourneySteps.push(step);
    }
  },
  removeCurrentJourneySteps: (state) => {
    state.currentJourneySteps = [];
  },
  removeCurrentJourneyStep: (state, key) => {
    const index = state.currentJourneySteps.findIndex(step => step.key === key);
    if (index !== -1) {
      state.currentJourneySteps.splice(index, 1);
    }
  },
  updateCurrentJourneyStep: (state, updatedStep) => {
    const index = state.currentJourneySteps.findIndex(step => step.key === updatedStep.key);
    if (index !== -1) {
      Vue.set(
        state.currentJourneySteps,
        index,
        { ...state.currentJourneySteps[index], ...updatedStep }
      )
    }
  },
  setSteps: (state, steps) => {
    state.steps = steps
  },
  setIsCreateFromDefault: (state, isCreateFromDefault) => {
    state.isCreateFromDefault = isCreateFromDefault;
  },
  setNeedToRefreshCanvas: (state, needToRefreshCanvas) => {
    state.needToRefreshCanvas = needToRefreshCanvas;
  },
}

const helper = {
  formatError(error, name) {
    if (error.response) {
      return error.response.data;
    }
    return error.message || `Something went wrong in ${name}`;
  }
}

const newJourney = () => ({
  "id": null,
  "name": null,
  "description": null,
  "key": null,
  "metadata": {
    "title": null,
    "logo": null,
    "typography": {
      "font": {
        "family": null,
        "size": "0px",
        "weight": null,
        "color": "#000000",
      }
    },
    "color": {
      "primary": "#000000",
      "secondary": "#000000",
      "tertiary": "#000000",
      "quaternary": "#000000",
      "background": {
        "primary": "#000000",
        "secondary": "#000000",
      }
    },
    "display": {
      "header": true,
    },
  }
})

const newStep = () => ({
  "id": null,
  "name": "",
  "key": "",
  "parent_id": "",
  "metadata": {
    "details": {
      "title": "",
      "description": "",
    },
    "conditions": [],
  }
})

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
